import React, { useState } from "react";
import axios from "axios";
import API_BASE from "../utils/api";
import Modal from "./Modal";

const DEVICE_TYPES = [
  { value: "bulk_import", label: "Manual Roster File" },
  { value: "device_biometric", label: "Biometric Device Export" },
  { value: "device_rfid", label: "RFID Device Export" },
];

const downloadTemplate = () => {
  const rows = [
    ["rollNumber", "date", "status", "period", "remarks"],
    ["R001", "2026-06-01", "present", "0", ""],
  ];
  const csv = rows.map((r) => r.join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "attendance_import_template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// Bulk-import attendance from an Excel export — either a manual roster file
// or a biometric/RFID device's scan log. Modeled directly on the bulk-import
// modal in pages/admin/Students.jsx (file input, template download, results table).
const BulkAttendanceImportModal = ({ onClose, onImported }) => {
  const [deviceType, setDeviceType] = useState("bulk_import");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError("");
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("deviceType", deviceType);
      const res = await axios.post(`${API_BASE}/attendance/bulk-import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      onImported && onImported();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal title="Bulk Import Attendance" subtitle="Upload a device export or roster file" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Upload an Excel (.xlsx/.xls) file. Each row identifies a student by <strong>rollNumber</strong>, <strong>admissionNumber</strong>,
          or (for device exports) <strong>rfidCardId</strong>/<strong>biometricId</strong>, plus a <strong>date</strong>. <strong>status</strong> and{" "}
          <strong>period</strong> are optional — status defaults to "present" (a device scan usually just means "seen"), period defaults to 0 (whole day).
        </p>
        <button type="button" onClick={downloadTemplate} className="text-sm text-indigo-600 hover:underline">
          Download CSV template
        </button>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
          <select value={deviceType} onChange={(e) => setDeviceType(e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm">
            {DEVICE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); setError(""); }}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
        {result && (
          <div className="space-y-2">
            <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {result.created} of {result.total} record(s) imported successfully.
              {result.failed > 0 && ` ${result.failed} failed.`}
            </div>
            {result.failed > 0 && (
              <div className="max-h-40 overflow-y-auto border rounded-lg divide-y text-sm">
                {result.results.filter((r) => r.status === "error").map((r, i) => (
                  <div key={i} className="px-3 py-2 text-red-600">
                    Row {r.row} ({r.identifier || "unknown"}): {r.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100">Close</button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!file || importing}
            className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {importing ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkAttendanceImportModal;
